/**
 * File Manager "create" flows: new file locations, URL locations, directory
 * locations, and single-file uploads. Deletes / updates live in
 * `files-crud.ts`; conflict-detection helpers live in `files-conflict.ts`.
 */

import getRouterConfig from "@hooks/useRouter";
import {
  showSpinner,
  stopSpinner,
  setToastMessage,
  reportError,
  closeDock,
  updateMarketingItem,
  clearFormFields,
  enableSave,
  toggleModal,
} from "@actions/global";
import {
  addFileLocation,
  setLocationName,
} from "@features/files/actions";
import { calculateOffsetLimit } from "@utils/pagination-helpers";
import { googleAnalyticsEventSelection } from "@components/shared/google-analytics-events";
import { removeSetOfKeysFromObject } from "@utils/object-helpers";
import { getArrItemByKey } from "@utils/search-helpers";
import { userErrorMessage } from "@utils/custom-errors";
import { runAsyncAction } from "@utils/runAsyncAction";
import {
  initialFilesConfig,
  getFileManagerAdapter,
  timeEnrichmentBeforeSend,
  transformFileLocation,
  type Store,
} from "./files-helpers";
import type { FileThunk } from "./files-init";

/**
 * Creates a new file location (bucket / URL / directory) and dispatches
 * follow-up flows: directory + URL locations may immediately trigger a
 * `saveFiles()` upload if `fileDetails` was provided.
 */
export const saveFileLocation = (
  fileLocationDetails: any,
  action: string,
  fileDetails: any = {}
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };
  store.dispatch(showSpinner());
  const filesAdapter = await getFileManagerAdapter(store);

  const result =
    action === "createFileLocation" || action === "createUrlLocation"
      ? await filesAdapter.postNewFileLocation(fileLocationDetails, {})
      : action === "createDirectoryLocation"
        ? await filesAdapter.postNewFileLocation(fileLocationDetails, {
            directoryLocation: fileLocationDetails,
          })
        : null;
  const newFileLocation =
    result && result.success
      ? {
          location_name: result.file_location_data.file_location,
          file_location_id: result.file_location_data.file_location_id,
          files: [],
          multifile: false,
          showFiles: false,
          display_file_location: `${result.file_location_data.file_manager_base_url}${result.file_location_data.file_location}`,
          directoryLocation:
            action === "createDirectoryLocation" ? fileLocationDetails : null,
        }
      : null;
  store.dispatch(toggleModal(false));
  store.dispatch(enableSave());
  store.dispatch(closeDock());
  if (newFileLocation) {
    store.dispatch(addFileLocation(newFileLocation));
    if (action === "createDirectoryLocation") {
      store.dispatch(setLocationName(fileLocationDetails));
      saveFiles(fileDetails, true)(dispatch, getState);
    }
    if (action === "createUrlLocation") {
      store.dispatch(setLocationName(fileLocationDetails));
      if (fileDetails.file_data) {
        saveFiles(fileDetails, false)(dispatch, getState);
      } else {
        store.dispatch(stopSpinner());
        store.dispatch(clearFormFields());
        // @ts-expect-error - history is possibly null
        const { history } = getRouterConfig();
        store.dispatch(
          setToastMessage(
            `URL ${fileLocationDetails} has been created`,
            history ? history.push("/files") : null
          )
        );
      }
    }
    googleAnalyticsEventSelection("Create", {
      id: newFileLocation.file_location_id,
      payload: newFileLocation,
      category: "File Manager",
    });
  } else {
    store.dispatch(
      reportError(
        userErrorMessage("create", `URL ${fileLocationDetails}`, result)
      )
    );
    store.dispatch(stopSpinner());
  }
};

/**
 * Uploads a new file (single or multifile). On success dispatches the
 * marketing-item update, clears the form, and redirects to `/files`.
 */
export const saveFiles = (
  fileDetails: any,
  multifile: boolean = false
): FileThunk => async (dispatch, getState) => {
  const store: Store = { dispatch, getState };

  await runAsyncAction(
    dispatch,
    async () => {
      const { fileNewView, fileLocations, fileAllView } = store.getState();
      const stripped = timeEnrichmentBeforeSend(fileDetails);
      const filesAdapter = await getFileManagerAdapter(store);

      const apiResult = await filesAdapter.postNewFile(
        fileNewView.location_name,
        { ...stripped, file_state: 1, multifile }
      );
      if (!(apiResult.success || apiResult.file)) throw apiResult;

      const newFile = apiResult.file;
      const singleFileLocation = await filesAdapter.fetchAllFiles(
        calculateOffsetLimit(
          {
            ...initialFilesConfig,
            status: "live",
            file_location: newFile.location,
          },
          fileLocations.total
        )
      );
      let updatedFileLocation =
        singleFileLocation && singleFileLocation.file_locations
          ? transformFileLocation(
              singleFileLocation.file_locations,
              fileLocations.fileManagerBaseUrl,
              fileAllView.filesExtensionsToExclude
            )
          : [];
      updatedFileLocation = getArrItemByKey(
        "location_name",
        newFile.location,
        updatedFileLocation
      );
      store.dispatch(
        updateMarketingItem("file_location", updatedFileLocation, "update")
      );
      store.dispatch(clearFormFields());
      googleAnalyticsEventSelection("Create", {
        id: newFile.file_id,
        payload: removeSetOfKeysFromObject(newFile, ["file_data"]),
        category: "File Manager",
      });
      return newFile;
    },
    {
      errorAction: "create",
      errorSubject: "file",
      onSuccess: (newFile) => {
        // @ts-expect-error - history is possibly null
        const { history } = getRouterConfig();
        store.dispatch(
          setToastMessage(
            `File ${(newFile as any).file_name} has been saved`,
            history ? history.push("/files") : null
          )
        );
      },
      onFailure: () => {
        store.dispatch(enableSave());
      },
    }
  );
};
